import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowLeft, Palette, Type } from "lucide-react";
import { Monogram } from "@/components/site/Logo";

const BASE = "/brand";

const GROUPS = [
  {
    id: "primary",
    title: "Primary Logo",
    desc: "Horizontal lockup — use as the main logo on websites, letterheads and email signatures.",
    items: [
      { label: "On dark", file: "quantum-logo-horizontal-dark", bg: "#050505", wide: true },
      { label: "On light", file: "quantum-logo-horizontal-light", bg: "#ffffff", wide: true },
      { label: "Transparent", file: "quantum-logo-horizontal-transparent", bg: "checker", wide: true },
    ],
  },
  {
    id: "stacked",
    title: "Stacked Logo",
    desc: "Vertical lockup — ideal for square spaces, posters and social posts.",
    items: [
      { label: "On dark", file: "quantum-logo-stacked-dark", bg: "#050505" },
      { label: "Transparent", file: "quantum-logo-stacked-transparent", bg: "checker" },
    ],
  },
  {
    id: "icon",
    title: "Profile Icon / Monogram",
    desc: "The Q mark — perfect for social profile pictures, favicons and app icons (square).",
    items: [
      { label: "On dark", file: "quantum-icon-dark", bg: "#050505" },
      { label: "On gold", file: "quantum-icon-gold", bg: "#c2a878" },
      { label: "Transparent", file: "quantum-icon-transparent", bg: "checker" },
    ],
  },
  {
    id: "social",
    title: "Social Media Covers",
    desc: "Ready-sized cover/banner images — upload straight to your profiles (PNG).",
    items: [
      { label: "LinkedIn cover · 1128×191", file: "social-linkedin-cover", bg: "#050505", wide: true, pngOnly: true },
      { label: "X / Twitter header · 1500×500", file: "social-twitter-header", bg: "#050505", wide: true, pngOnly: true },
      { label: "Facebook cover · 820×312", file: "social-facebook-cover", bg: "#050505", wide: true, pngOnly: true },
    ],
  },
];

const PALETTE = [
  { name: "Obsidian", hex: "#050505" },
  { name: "Gold", hex: "#c2a878" },
  { name: "Off White", hex: "#f8f8f8" },
  { name: "Muted", hex: "#a3a3a3" },
];

const checkerStyle = {
  backgroundImage:
    "linear-gradient(45deg,#1a1a1a 25%,transparent 25%),linear-gradient(-45deg,#1a1a1a 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#1a1a1a 75%),linear-gradient(-45deg,transparent 75%,#1a1a1a 75%)",
  backgroundSize: "24px 24px",
  backgroundPosition: "0 0,0 12px,12px -12px,-12px 0",
  backgroundColor: "#0f0f0f",
};

const AssetCard = ({ item }) => {
  const bgStyle = item.bg === "checker" ? checkerStyle : { backgroundColor: item.bg };
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="border border-white/10 bg-quantum-bg2 overflow-hidden group"
      data-testid={`asset-${item.file}`}
    >
      <div
        className={`grid place-items-center p-10 ${item.wide ? "min-h-[180px]" : "min-h-[240px]"}`}
        style={bgStyle}
      >
        <img
          src={`${BASE}/${item.file}.${item.pngOnly ? "png" : "svg"}`}
          alt={item.label}
          className={item.wide ? "w-full max-w-[380px]" : "w-[180px] h-[180px] object-contain"}
        />
      </div>
      <div className="flex items-center justify-between px-5 py-4 border-t border-white/10">
        <span className="text-sm text-quantum-text">{item.label}</span>
        <div className="flex items-center gap-2">
          {!item.pngOnly && (
            <a
              href={`${BASE}/${item.file}.svg`}
              download
              data-testid={`download-${item.file}-svg`}
              className="flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 border border-white/15 text-quantum-muted hover:text-quantum-gold hover:border-quantum-gold transition-colors"
            >
              <Download size={13} /> SVG
            </a>
          )}
          <a
            href={`${BASE}/${item.file}.png`}
            download
            data-testid={`download-${item.file}-png`}
            className="flex items-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 bg-quantum-gold text-black hover:bg-white transition-colors"
          >
            <Download size={13} /> PNG
          </a>
        </div>
      </div>
    </motion.div>
  );
};

export default function Brand() {
  return (
    <div className="min-h-screen bg-quantum-bg text-quantum-text">
      <header className="flex items-center justify-between px-6 md:px-12 lg:px-24 py-6 border-b border-white/10">
        <Link to="/" data-testid="back-home" className="flex items-center gap-2 text-quantum-muted hover:text-quantum-gold transition-colors">
          <ArrowLeft size={18} /> <span className="text-sm">Back to site</span>
        </Link>
        <div className="flex items-center gap-3">
          <Monogram size={32} />
          <span className="font-serif-q text-xl">Brand Kit</span>
        </div>
      </header>

      <section className="px-6 md:px-12 lg:px-24 pt-20 pb-12 max-w-4xl">
        <p className="text-xs uppercase tracking-[0.3em] text-quantum-gold mb-5">Brand assets</p>
        <h1 className="font-serif-q font-light text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-6">
          Qauntum International logo kit
        </h1>
        <p className="text-lg text-quantum-muted leading-relaxed">
          Download the official logo in every format you need for social media, print and web.
          Use <span className="text-quantum-text">SVG</span> for crisp, resizable graphics and{" "}
          <span className="text-quantum-text">PNG</span> where images are required (most social
          platforms). Transparent versions sit cleanly on any background.
        </p>
      </section>

      <div className="px-6 md:px-12 lg:px-24 pb-24 space-y-20">
        {GROUPS.map((g) => (
          <section key={g.id} data-testid={`group-${g.id}`}>
            <div className="mb-8 max-w-2xl">
              <h2 className="font-serif-q text-3xl md:text-4xl font-light mb-2">{g.title}</h2>
              <p className="text-quantum-muted">{g.desc}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {g.items.map((item) => (
                <AssetCard key={item.file} item={item} />
              ))}
            </div>
          </section>
        ))}

        <section data-testid="brand-guidelines" className="grid md:grid-cols-2 gap-12 pt-8 border-t border-white/10">
          <div>
            <div className="flex items-center gap-2 mb-6">
              <Palette size={18} className="text-quantum-gold" />
              <h2 className="font-serif-q text-3xl font-light">Colour palette</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {PALETTE.map((c) => (
                <div key={c.hex} data-testid={`swatch-${c.hex}`}>
                  <div className="h-24 border border-white/10" style={{ backgroundColor: c.hex }} />
                  <p className="mt-2 text-sm text-quantum-text">{c.name}</p>
                  <p className="text-xs text-quantum-muted uppercase">{c.hex}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-6">
              <Type size={18} className="text-quantum-gold" />
              <h2 className="font-serif-q text-3xl font-light">Typography</h2>
            </div>
            <div className="space-y-6">
              <div className="border border-white/10 p-6">
                <p className="font-serif-q text-4xl">Cormorant Garamond</p>
                <p className="text-quantum-muted text-sm mt-2">Headlines & the wordmark</p>
              </div>
              <div className="border border-white/10 p-6">
                <p className="text-2xl">Outfit</p>
                <p className="text-quantum-muted text-sm mt-2">Body text & labels</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
